import { FC, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import useTranslation from 'next-translate/useTranslation'
import { Box } from '@/components/atoms/Grid'
import { H1 } from '@/components/atoms/typography'
import Link from '@/components/atoms/link'
import { Manufacturer } from '@/network/graphql.g'
import { validFor } from '@/theme/validFor'
import { applyImagePolicy } from '@/utils/thumborUtils'

type TopManufacturerMarqueeProps = {
  manufacturers: Manufacturer[]
  hideTitle?: boolean
} & Rest

const secondsPerLogo = 3.125
const minLogosPerSet = 12
const slotWidth = { desktop: 160, tablet: 140, mobile: 110 }

const TopManufacturerMarquee: FC<TopManufacturerMarqueeProps> = ({
  manufacturers,
  hideTitle = false,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const [isTouched, setIsTouched] = useState<boolean>(false)

  if (!manufacturers?.length) {
    return null
  }

  const release = () => setIsTouched(false)
  const repeats = Math.ceil(minLogosPerSet / manufacturers.length)
  const logoSet = Array.from({ length: repeats }, (_, repeat) =>
    manufacturers.map((manufacturer) => ({ manufacturer, repeat }))
  ).flat()

  return (
    <Box {...rest}>
      {!hideTitle && (
        <StyledTitleBox>
          <StyledH1>{t('HomePage.Sections.TopManufacturers.title')}</StyledH1>
        </StyledTitleBox>
      )}
      <Viewport
        data-testid="topManufacturerMarquee"
        onTouchStart={() => setIsTouched(true)}
        onTouchEnd={release}
        onTouchCancel={release}
      >
        <Track
          $duration={logoSet.length * secondsPerLogo}
          $logoCount={logoSet.length}
          $paused={isTouched}
        >
          {[false, true].map((isCopy) =>
            logoSet.map(({ manufacturer, repeat }) => {
              const isDuplicate = isCopy || repeat > 0
              return (
                <LogoLink
                  key={`${isCopy ? 'copy' : 'main'}-${repeat}-${
                    manufacturer.id
                  }`}
                  href={manufacturer.link}
                  aria-hidden={isDuplicate || undefined}
                  tabIndex={isDuplicate ? -1 : undefined}
                  data-copy={isDuplicate || undefined}
                >
                  <LogoImage
                    src={applyImagePolicy(manufacturer.image, { useCdn: true })}
                    alt={manufacturer.name ?? ''}
                    decoding="async"
                  />
                </LogoLink>
              )
            })
          )}
        </Track>
      </Viewport>
    </Box>
  )
}

export default TopManufacturerMarquee

const scroll = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(-50%, 0, 0); }
`

const StyledTitleBox = styled(Box)`
  text-align: left;
  width: 100%;
  margin-top: 30px;
  ${validFor.tablet`
    margin-top: 20px;
  `}
  ${validFor.mobile`
    margin-top: 15px;
  `}
`

const StyledH1 = styled(H1)`
  ${validFor.tablet`
    font-size: 30px;
  `}
  ${validFor.mobile`
    font-size: 20px;
  `}
`

const Viewport = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 8px 0;

  &:hover > div,
  &:focus-within > div {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    overflow-x: auto;
  }
`

const Track = styled.div<{
  $duration: number
  $logoCount: number
  $paused: boolean
}>`
  display: flex;
  width: max-content;
  will-change: transform;
  backface-visibility: hidden;
  animation: ${scroll} ${({ $duration }) => $duration}s linear infinite;
  ${({ $logoCount }) => css`
    animation-timing-function: steps(${$logoCount * slotWidth.desktop});
    ${validFor.tablet`
      animation-timing-function: steps(${$logoCount * slotWidth.tablet});
    `}
    ${validFor.mobile`
      animation-timing-function: steps(${$logoCount * slotWidth.mobile});
    `}
  `}
  ${({ $paused }) =>
    $paused &&
    css`
      animation-play-state: paused;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;

    & > [data-copy] {
      display: none;
    }
  }
`

const LogoLink = styled(Link)`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: ${slotWidth.desktop}px;
  height: 61px;
  padding: 8px 20px;
  box-sizing: border-box;
  ${validFor.tablet`
    width: ${slotWidth.tablet}px;
  `}
  ${validFor.mobile`
    width: ${slotWidth.mobile}px;
    padding: 8px 12px;
  `}

  &:hover img,
  &:focus-visible img {
    transform: scale(1.2);
  }
`

const LogoImage = styled.img`
  display: block;
  width: 100%;
  height: 45px;
  object-fit: contain;
  transition: transform 0.2s ease-out;
`
